import { Component, Input, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { endOfYear, isAfter, isBefore, startOfYear } from 'date-fns';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { firstValueFrom } from 'rxjs';

import { BudgetService } from '../../../budget/budget.service';
import { LinkedFileToObject } from '../../../models/LinkedFileToObject';
import { LinkedFilesToObjectWithId } from '../../../models/LinkedFilesToObjectWithId';
import { InvoiceInformationModalComponent } from '../../../yuki/invoice-information-modal/invoice-information-modal.component';
import { AddFileToObjectModalComponent } from '../add-file-to-object-modal/add-file-to-object-modal.component';
import { AddChildLinkModalComponent } from '../add-child-link-modal/add-child-link-modal.component';
import { DeleteLinkedObject, DeleteSubLinkToLinkedObjectToFile, GetAllFilesLinkedToObject } from '../global-files.actions';
import { GlobalFilesState } from '../global-files.state';
import { UploadedDocumentDetailComponent } from '../uploaded-document-detail/uploaded-document-detail.component';
import { FinancialService } from '../../../financial/financial.service';
import { InformationForFinancialData } from '../../../models/InformationForFinancialData';
import { GlobalFilesService } from '../global-files.service';

@Component({
    selector: 'app-linked-invoices-to-object',
    templateUrl: './linked-invoices-to-object.component.html',
    styleUrls: ['./linked-invoices-to-object.component.scss'],
})
export class LinkedInvoicesToObjectComponent implements OnInit, OnDestroy, AfterViewInit {
    readonly projectFileCategories: { value: string; label: string }[] = [
        { value: 'PO', label: 'Purchase order (PO)' },
        { value: 'SES', label: 'Toelating tot facturatie (SES)' },
        { value: 'MSA', label: 'Master Service Agreement (MSA)' },
        { value: 'OTHER', label: 'Other' },
    ];
    private categoryByFileId: Record<string, string> = {};
    extractingByFileId: Record<string, boolean> = {};
    updatingPeriodByFileId: Record<string, boolean> = {};
    @Input() objectId: string;
    @Input() showFiles = true;
    @Input() showInvoices = true;
    @Input() alreadyFetchedFiles = false;
    @Input() folderId: number;
    @Input() enableBudgetLink = false;
    @Select(GlobalFilesState.LinkedFilesToObjectWithId) linkedFilesToObjectWithId$: Observable<LinkedFilesToObjectWithId>;

    linkedFilesToObjectWithId: LinkedFilesToObjectWithId;
    datasource: MatTableDataSource<LinkedFileToObject> = new MatTableDataSource<LinkedFileToObject>();
    displayedColumns: string[] = ['file'];
    startDate: Date = startOfYear(new Date());
    endDate: Date = endOfYear(new Date());
    
    // Map fileId -> allocated amount (from Financial API which has correct allocation logic)
    private allocatedAmountsMap: Map<string, number> = new Map();

    @ViewChild(MatSort) sort: MatSort;

    private subscriptions = new Subscription();

    constructor(
        private store: Store,
        private logger: NGXLogger,
        private toastr: ToastrService,
        private modalService: NgbModal,
        private budgetService: BudgetService,
        private financialService: FinancialService,
        private globalFilesService: GlobalFilesService,
    ) {}

    ngOnInit(): void {
        this.createMatTable();
        this.loadAllocatedAmounts();
        this.fetchData();
    }

    ngAfterViewInit() {
        // Set sort first
        this.datasource.sort = this.sort;
        
        // Ensure sortingDataAccessor is set after sort is attached
        this.datasource.sortingDataAccessor = (item: LinkedFileToObject, property: string) => {
            switch (property) {
                case 'file':
                    return item.title?.toLowerCase() || '';
                case 'amount':
                    return this.getAllocatedAmount(item);
                case 'date':
                    // Convert date to timestamp for proper numeric sorting
                    return item.invoiceDate ? new Date(item.invoiceDate).getTime() : 0;
                case 'invoiceNumber':
                    return item.invoiceNumber?.toLowerCase() || '';
                case 'description':
                    return item.description?.toLowerCase() || '';
                case 'category':
                    return item.documentType?.toLowerCase() || '';
                case 'fileNumber':
                    return item.extractedFileNumber?.toLowerCase() || '';
                case 'period':
                    return item.extractedPeriodFrom || item.extractedPeriodTo || '';
                default:
                    return (item as any)[property];
            }
        };
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    filterOnDate(startDate: Date, endDate: Date) {
        this.startDate = startDate;
        this.endDate = endDate;
        this.executeFilter();
    }

    deleteLink(linkedFile: LinkedFileToObject) {
        if (linkedFile.sublink) {
            this.subscriptions.add(
                this.store.dispatch(new DeleteSubLinkToLinkedObjectToFile(linkedFile.childLinkId)).subscribe({
                    next: () => {
                        this.toastr.success('Successfully deleted object from ' + linkedFile.title);
                        this.logger.debug('Successfully deleted object from ' + linkedFile.title);
                    },
                    error: () => {
                        this.toastr.error('Failed to link file with current object');
                        this.logger.error('Failed fetch file of Yuki document due to request error to the Yuki API.');
                    },
                }),
            );
            return;
        }
        this.subscriptions.add(
            this.store.dispatch(new DeleteLinkedObject(linkedFile.fileId, this.objectId)).subscribe({
                next: () => {
                    this.toastr.success('Successfully deleted object from ' + linkedFile.title);
                    this.logger.debug('Successfully deleted object from ' + linkedFile.title);
                },
                error: () => {
                    this.toastr.error('Failed to link file with current object');
                    this.logger.error('Failed fetch file of Yuki document due to request error to the Yuki API.');
                },
            }),
        );
    }

    openInvoiceInformationModal(linkedFile: LinkedFileToObject) {
        if (!(linkedFile.fileType || '').toLowerCase().includes('uploadeddocument')) {
            const modalRef = this.modalService.open(InvoiceInformationModalComponent, {
                windowClass: 'modal-full-screen',
                animation: false,
            });
            modalRef.componentInstance.yukiDocumentId = linkedFile.fileId;
            modalRef.componentInstance.linkedObjectId = this.objectId;
            modalRef.componentInstance.linkedIsSublink = !!linkedFile.sublink;
            modalRef.componentInstance.linkedChildLinkId = linkedFile.childLinkId;
        } else {
            const modalRef = this.modalService.open(UploadedDocumentDetailComponent, {
                windowClass: 'modal-huge',
                animation: false,
            });
            modalRef.componentInstance.uploadedDocumentId = linkedFile.fileId;
        }
    }

    openAddDocumentModal() {
        const knownFileIds = new Set((this.datasource.data || []).map((linkedFile) => linkedFile.fileId).filter(Boolean));

        const modalRef = this.modalService.open(AddFileToObjectModalComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });
        modalRef.componentInstance.objectId = this.objectId;
        if (this.showFiles && !this.showInvoices) {
            modalRef.componentInstance.allowedDocumentTypes = this.projectFileCategories;
            modalRef.componentInstance.requireDocumentType = true;
        }

        modalRef.result.then(
            () => this.autoExtractNewlyUploadedDocument(knownFileIds),
            () => {
                /* upload was cancelled, nothing to auto-extract */
            },
        );
    }

    /**
     * PO/SES documents are only useful once AI extraction has run (PO/SES numbers, amounts, ...).
     * Run it automatically right after upload instead of requiring a separate manual click.
     */
    private autoExtractNewlyUploadedDocument(knownFileIds: Set<string>): void {
        const newFile = (this.datasource.data || []).find(
            (linkedFile) => linkedFile.fileId && !knownFileIds.has(linkedFile.fileId) && this.isUploadedDocument(linkedFile),
        );
        if (!newFile) return;

        const documentType = (this.getDocumentTypeValue(newFile) || '').toUpperCase();
        if ((documentType === 'PO' || documentType === 'SES') && newFile.extractionStatus !== 'SUCCESS') {
            this.toastr.info('Document geüpload. AI extractie wordt gestart.');
            this.extractProjectDocument(newFile);
        }
    }

    isUploadedDocument(linkedFile: LinkedFileToObject): boolean {
        return (linkedFile?.fileType || '').toLowerCase().includes('uploadeddocument');
    }

    getDocumentTypeValue(linkedFile: LinkedFileToObject): string {
        if (!linkedFile?.fileId) {
            return linkedFile?.documentType || '';
        }
        const cachedCategory = this.categoryByFileId[linkedFile.fileId];
        if (cachedCategory !== undefined) {
            return cachedCategory;
        }
        return linkedFile.documentType || '';
    }

    updateProjectFileCategory(linkedFile: LinkedFileToObject, nextCategory: string, event?: Event): void {
        if (event && typeof (event as any).stopPropagation === 'function') {
            event.stopPropagation();
        }

        if (!this.isUploadedDocument(linkedFile) || !linkedFile?.fileId) {
            return;
        }

        const normalizedNextCategory = (nextCategory || '').trim();
        if (!normalizedNextCategory) {
            this.toastr.warning('Category is required');
            return;
        }

        const previousCategory = this.getDocumentTypeValue(linkedFile);
        if (previousCategory === normalizedNextCategory) {
            return;
        }

        this.categoryByFileId[linkedFile.fileId] = normalizedNextCategory;
        this.globalFilesService.updateDocumentType(linkedFile.fileId, normalizedNextCategory).subscribe({
            next: () => {
                this.toastr.success('Category updated');
                const upper = normalizedNextCategory.toUpperCase();
                if ((upper === 'SES' || upper === 'PO') && linkedFile.extractionStatus !== 'SUCCESS') {
                    this.extractProjectDocument(linkedFile);
                }
            },
            error: () => {
                this.categoryByFileId[linkedFile.fileId] = previousCategory;
                this.toastr.error('Could not update category');
            },
        });
    }

    canExtract(linkedFile: LinkedFileToObject): boolean {
        return this.isUploadedDocument(linkedFile);
    }

    extractProjectDocument(linkedFile: LinkedFileToObject, event?: Event, silent = false): void {
        if (event) event.stopPropagation();
        if (!linkedFile?.fileId || this.extractingByFileId[linkedFile.fileId]) return;

        this.extractingByFileId[linkedFile.fileId] = true;
        this.globalFilesService.extractProjectDocument(linkedFile.fileId).subscribe({
            next: (result) => {
                this.extractingByFileId[linkedFile.fileId] = false;
                linkedFile.documentType = result.documentType || linkedFile.documentType;
                linkedFile.extractionStatus = result.extractionStatus || linkedFile.extractionStatus;
                linkedFile.extractedPoNumber = result.extractedPoNumber || null;
                linkedFile.extractedSesNumber = result.extractedSesNumber || null;
                linkedFile.extractedFileNumber = result.extractedFileNumber || null;
                linkedFile.extractedAmount = result.extractedAmount ?? null;
                linkedFile.extractedHours = result.extractedHours ?? null;
                linkedFile.extractedUnitRate = result.extractedUnitRate ?? null;
                linkedFile.extractedCurrency = result.extractedCurrency || null;
                linkedFile.extractedPeriodFrom = result.extractedPeriodFrom || null;
                linkedFile.extractedPeriodTo = result.extractedPeriodTo || null;
                linkedFile.extractedData = result.extractedData || {};
                if (result.documentType) {
                    this.categoryByFileId[linkedFile.fileId] = result.documentType;
                }
                if (!silent) this.toastr.success('AI extractie voltooid');
                // Refresh files list to get updated extraction data
                this.store.dispatch(new GetAllFilesLinkedToObject(this.objectId));
            },
            error: (err) => {
                this.extractingByFileId[linkedFile.fileId] = false;
                if (!silent) this.toastr.error('AI extractie mislukt: ' + (err?.error?.message || err?.message || 'Onbekende fout'));
            },
        });
    }

    updateProjectDocumentPeriod(linkedFile: LinkedFileToObject, periodFrom?: string, periodTo?: string): void {
        if (!linkedFile?.fileId || this.updatingPeriodByFileId[linkedFile.fileId]) return;

        const normalizedFrom = (periodFrom || '').trim();
        const normalizedTo = (periodTo || '').trim();

        linkedFile.extractedPeriodFrom = normalizedFrom || null;
        linkedFile.extractedPeriodTo = normalizedTo || null;
        this.updatingPeriodByFileId[linkedFile.fileId] = true;

        this.globalFilesService.updateProjectDocumentPeriod(linkedFile.fileId, normalizedFrom, normalizedTo).subscribe({
            next: (updatedDocument) => {
                this.updatingPeriodByFileId[linkedFile.fileId] = false;
                linkedFile.extractedPeriodFrom = updatedDocument.extractedPeriodFrom || null;
                linkedFile.extractedPeriodTo = updatedDocument.extractedPeriodTo || null;
                this.toastr.success('Periode bijgewerkt');
            },
            error: (err) => {
                this.updatingPeriodByFileId[linkedFile.fileId] = false;
                this.toastr.error('Periode kon niet worden bijgewerkt: ' + (err?.error?.message || err?.message || 'Onbekende fout'));
                this.store.dispatch(new GetAllFilesLinkedToObject(this.objectId));
            },
        });
    }

    calculateTotal(): number {
        return this.datasource.filteredData.reduce((total, linkedObjectToFile) => {
            const allocatedAmt = this.getAllocatedAmount(linkedObjectToFile);
            const signed = linkedObjectToFile.folderId === 1 ? -allocatedAmt : allocatedAmt;
            return total + (signed || 0);
        }, 0);
    }

    async openBudgetLinkModal(linkedFile: LinkedFileToObject, event?: Event): Promise<void> {
        if (event) {
            event.stopPropagation();
        }

        if (!linkedFile?.fileId || !this.objectId) {
            this.toastr.error('Could not open budget link modal.');
            return;
        }

        if (linkedFile.folderId !== 1) {
            this.toastr.info('Only purchase invoices can be linked to a budget expenditure.');
            return;
        }

        try {
            const linkedObjectsToFile = await firstValueFrom(this.globalFilesService.getAllObjectsLinkedToFile(linkedFile.fileId));
            const parentProjectLink = (linkedObjectsToFile?.linkedObjects || []).find((link) => link?.objectId === this.objectId);

            if (!parentProjectLink) {
                this.toastr.warning('This invoice is not directly linked to the current project.');
                return;
            }

            const modalRef = this.modalService.open(AddChildLinkModalComponent, {
                windowClass: 'modal-prompt modal-prompt--min-content',
                animation: false,
            });
            modalRef.componentInstance.link = parentProjectLink;
            modalRef.componentInstance.file = {
                uuid: linkedFile.fileId,
                folderId: linkedFile.folderId,
                documentDate: linkedFile.invoiceDate,
            } as any;
            modalRef.componentInstance.allowedTypes = ['budget'];
            modalRef.componentInstance.defaultType = 'budget';
        } catch (error) {
            this.logger.error('Opening budget link modal failed', error);
            this.toastr.error('Could not load invoice link details.');
        }
    }
    
    getAllocatedAmount(linkedFile: LinkedFileToObject): number {
        // Prefer the allocated amount from Financial API (which uses correct child link logic)
        if (this.allocatedAmountsMap.has(linkedFile.fileId)) {
            return this.allocatedAmountsMap.get(linkedFile.fileId)!;
        }
        // Fallback to price if not found in map
        return linkedFile.price || 0;
    }

    getExtractionTooltip(linkedFile: LinkedFileToObject): string | null {
        if (!this.isUploadedDocument(linkedFile)) return null;
        if (linkedFile.extractionStatus !== 'SUCCESS') return null;

        const lines: string[] = [];
        if (linkedFile.extractedPoNumber) lines.push(`PO: ${linkedFile.extractedPoNumber}`);
        if (linkedFile.extractedSesNumber) lines.push(`SES: ${linkedFile.extractedSesNumber}`);
        if (linkedFile.extractedFileNumber) lines.push(`File nr: ${linkedFile.extractedFileNumber}`);
        if (linkedFile.extractedAmount != null) {
            const currency = linkedFile.extractedCurrency || 'EUR';
            lines.push(`Bedrag: ${linkedFile.extractedAmount} ${currency}`);
        }
        if (linkedFile.extractedPeriodFrom || linkedFile.extractedPeriodTo) {
            const from = linkedFile.extractedPeriodFrom || '?';
            const to = linkedFile.extractedPeriodTo || '?';
            lines.push(`Periode: ${from} — ${to}`);
        }
        return lines.length > 0 ? lines.join('\n') : null;
    }

    getBase64(url: string) {
        return this.budgetService.getInvoice(url);
    }
    
    private loadAllocatedAmounts(): void {
        const year = this.startDate.getFullYear();
        // Fetch financial information for all 12 months to build the allocation map
        const requests = [];
        for (let month = 1; month <= 12; month++) {
            requests.push(this.financialService.getAllFinancialInformationForEmployee(this.objectId, month, year));
        }
        
        this.subscriptions.add(
            forkJoin(requests).subscribe({
                next: (results: InformationForFinancialData[][]) => {
                    // Flatten all months and build a map of fileId -> allocated amount
                    results.forEach(monthData => {
                        monthData.forEach(info => {
                            if (info.invoiceType) {
                                // Only store invoice data, not budget data
                                this.allocatedAmountsMap.set(info.objectId, info.amount);
                            }
                        });
                    });
                    this.logger.debug('Loaded allocated amounts for', this.allocatedAmountsMap.size, 'invoices');
                },
                error: (err) => {
                    this.logger.error('Failed to load allocated amounts:', err);
                }
            })
        );
    }

    private executeFilter() {
        const filter: LinkedInvoicesListFilter = {
            showFiles: this.showFiles,
            showInvoices: this.showInvoices,
            folderId: this.folderId,
            startDate: this.startDate,
            endDate: this.endDate,
        };
        this.datasource.filter = JSON.stringify(filter);
    }

    private fetchData() {
        if (!this.alreadyFetchedFiles) {
            this.logger.debug('Fetching all files linked to object');
            this.store.dispatch(new GetAllFilesLinkedToObject(this.objectId));
        }
        this.subscriptions.add(
            this.linkedFilesToObjectWithId$.subscribe((linkedFilesToObjectWithId) => {
                this.linkedFilesToObjectWithId = linkedFilesToObjectWithId;
                    const linkedFiles = (linkedFilesToObjectWithId?.linkedFiles || []).map((linkedFile) => ({ ...linkedFile }));
                    this.datasource.data = linkedFiles;
                    linkedFiles.forEach((linkedFile) => {
                        if (this.isUploadedDocument(linkedFile)) {
                            this.categoryByFileId[linkedFile.fileId] = linkedFile.documentType || '';
                        }
                    });
                this.datasource.sort = this.sort;
                this.executeFilter();
            }),
        );
    }

    private createMatTable() {
        if (this.showFiles) this.displayedColumns.push('category');
        if (this.showFiles) this.displayedColumns.push('fileNumber');
        if (this.showFiles) this.displayedColumns.push('period');
        if (this.showFiles) this.displayedColumns.push('extract');
        if (this.showInvoices) this.displayedColumns.push('amount', 'date');
        if (this.showInvoices && this.folderId === 2) this.displayedColumns.push('invoiceNumber');
        if (this.showInvoices && this.enableBudgetLink && this.folderId === 1) this.displayedColumns.push('budgetLink');

        this.displayedColumns.push('delete');

        this.datasource.filterPredicate = function (data, filter) {
            const filterObj: LinkedInvoicesListFilter = JSON.parse(filter);
            // Exclude archived/deleted invoices if detectable via metadata
            const type = (data.invoiceType || '').toLowerCase();
            const desc = (data.description || '').toLowerCase();
            const title = (data.title || '').toLowerCase();
            const isArchivedOrDeleted =
                type.includes('archived') ||
                type.includes('deleted') ||
                desc.includes('archived') ||
                desc.includes('deleted') ||
                title.includes('archived') ||
                title.includes('deleted');

            return (
                (filterObj.showInvoices &&
                    (data.fileType || '').toLowerCase() === 'yukidocument' &&
                    data.folderId === filterObj.folderId &&
                    isBefore(new Date(data.invoiceDate), new Date(filterObj.endDate)) &&
                    isAfter(new Date(data.invoiceDate), new Date(filterObj.startDate)) &&
                    !isArchivedOrDeleted) ||
                (filterObj.showFiles && (data.fileType || '').toLowerCase().includes('uploadeddocument'))
            );
        };
    }
}

class LinkedInvoicesListFilter {
    showInvoices: boolean;
    showFiles: boolean;
    folderId: number;
    startDate: Date;
    endDate: Date;
}
